import type { CSSProperties } from "react";

/**
 * Shared pastel accent system for the homepage.
 *
 * Each accent maps onto the existing `--qa-*` design tokens (light + dark
 * variants already defined in `src/styles.css`), so cards stay calming and
 * on-brand rather than bright. Components set the accent with `accentVars()`
 * and then use `ACCENT_CARD_STYLE` / `ACCENT_CHIP_STYLE` for consistent
 * surfaces, borders and icon circles.
 */
export type AccentName =
  | "gold"
  | "sage"
  | "sky"
  | "mint"
  | "lavender"
  | "plum"
  | "rose"
  | "peach"
  | "teal"
  | "indigo";

export const ACCENT_ORDER: AccentName[] = [
  "gold",
  "sage",
  "lavender",
  "sky",
  "peach",
  "mint",
  "plum",
  "teal",
  "rose",
  "indigo",
];

/** CSS custom properties that scope an accent to a subtree. */
export function accentVars(accent: AccentName): CSSProperties {
  return {
    ["--acc-tint" as string]: `var(--qa-${accent}-tint)`,
    ["--acc-ink" as string]: `var(--qa-${accent}-ink)`,
  } as CSSProperties;
}

/** Rotate accents across a list so neighbouring cards differ. */
export function accentAt(index: number): AccentName {
  return ACCENT_ORDER[index % ACCENT_ORDER.length]!;
}

/** Warm, softly tinted card surface. Requires `accentVars()` on the element. */
export const ACCENT_CARD_STYLE: CSSProperties = {
  background:
    "linear-gradient(160deg, color-mix(in oklab, var(--acc-tint) 92%, var(--card)) 0%, var(--card) 78%)",
  borderColor: "color-mix(in oklab, var(--acc-ink) 22%, transparent)",
};

/** Coloured icon circle. Requires `accentVars()` on an ancestor. */
export const ACCENT_CHIP_STYLE: CSSProperties = {
  background: "color-mix(in oklab, var(--acc-ink) 16%, var(--card))",
  color: "var(--acc-ink)",
  boxShadow: "inset 0 0 0 1px color-mix(in oklab, var(--acc-ink) 26%, transparent)",
};

/**
 * Neutral card surface for the homepage: colour is carried by the icons,
 * not the card itself.
 */
export const NEUTRAL_CARD_STYLE: CSSProperties = {
  background: "var(--card)",
  borderColor: "color-mix(in oklab, var(--foreground) 10%, transparent)",
};


/** Accent-coloured text (titles, small CTAs). */
export const ACCENT_INK_STYLE: CSSProperties = { color: "var(--acc-ink)" };

/** Soft tinted band used behind whole homepage sections. */
export function sectionTintStyle(accent: AccentName): CSSProperties {
  return {
    background: `linear-gradient(180deg, color-mix(in oklab, var(--qa-${accent}-tint) 70%, transparent) 0%, transparent 100%)`,
  };
}
